import * as React from 'react';

import {
    ISaveDocumentMetaData,
    useSaveDocumentMetaDataMutation,
} from './useSaveDocumentMetaDataMutation';

import CheckmateSelect from '../CheckmateSelect';
import CheckmateSelectHelper from '../../../utilities/CheckmateSelectHelper';
import { IDocumentModel } from '../../../interfaces/ICase';
import { IMultiSelectOption } from '../../../interfaces/ILookup';
import { IValidation } from '../../../interfaces/IError';
import { LoadingButton } from '../../../shared/buttons/LoadingButton';
import { Modal } from 'react-bootstrap';
import { cloneDeep } from 'lodash';
import { useDocumentCategoryTypes } from '../../../shared/react-query-hooks/useDocumentCategoryTypes';
import { useEffect } from 'react';
import { useZoneContext } from '../../../contexts/useZoneContext';

interface IDocumentEditModalProps {
    currentDocument: IDocumentModel;
    entityTypeId: number;
    onHide: () => void;
    onSaveComplete: (document: IDocumentModel) => void;
    open: boolean;
}

function DocumentEditModal(props: IDocumentEditModalProps) {
    if (!props.open) return null;

    const saveDocumentMutation = useSaveDocumentMetaDataMutation();

    const [local, setLocal] = React.useState<IDocumentModel>(cloneDeep(props.currentDocument));
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [validation, setValidation] = React.useState<IValidation>({});
    const { zoneGuid } = useZoneContext();
    const nameInputRef = React.useRef<HTMLInputElement>(null);

    const categoryDocumentTypes = useDocumentCategoryTypes(props.entityTypeId);
    const showDocumentCategory = (categoryDocumentTypes?.data?.length ?? 0) > 0;
    const categoryDocumentTypeOptions = CheckmateSelectHelper.getLookupOptions(
        categoryDocumentTypes.data ?? [],
        false
    );

    const validateForm = () => {
        let hasErrors = false;
        let localValidation = {} as IValidation;

        if (!local.fileName) {
            localValidation.filename = ['File Name is required '];
            hasErrors = true;
        }

        if (hasErrors) {
            setValidation(localValidation);
        }

        return !hasErrors;
    };

    const handleSaveDocument = async () => {
        if (!validateForm()) {
            return;
        }

        if (!local.guid || !local.fileName) {
            return;
        }

        const payload: ISaveDocumentMetaData = {
            documentGuid: local.guid,
            fileName: local.fileName,
            documentCategoryTypeIds: local.documentCategoryTypeIds ?? [],
        };

        saveDocumentMutation
            .mutateAsync(payload)
            .then((response) => {
                const validation: IValidation = {};
                props.onSaveComplete(response.payload?.data ?? local); // todo jec: remove local fallback after endpoint is updated to return DocumentModel
                setValidation(validation);
                props.onHide();
            })
            .catch((error: Error) => {
                validation.model = [error.message];
                setValidation(validation);
            });
    };

    const handleCancel = () => {
        props.onHide();
    };

    const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocal((prev) => ({ ...prev, fileName: e.target.value }));
    };

    const handleDocumentCategoriesChange = (options: IMultiSelectOption[]) => {
        const localCopy = cloneDeep(local);
        if (!options) {
            localCopy.documentCategoryTypeIds = [];
        } else {
            localCopy.documentCategoryTypeIds = options.map((x) => x.id);
        }
        setLocal(localCopy);
    };

    useEffect(() => {
        if (props.open && nameInputRef.current) {
            nameInputRef.current.select();
        }
    }, [props.open]);

    return (
        <Modal centered show={props.open} onHide={props.onHide} backdrop>
            <Modal.Header>
                <Modal.Title>Edit Document</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {validation.model && <div className="text-danger">{validation.model}</div>}

                <div className="form-group mb-2">
                    <label className="control-label" htmlFor="file">
                        File Name
                    </label>
                    <input
                        className="form-control"
                        disabled={isLoading}
                        name="title"
                        onChange={handleFileNameChange}
                        type="text"
                        value={local.fileName || ''}
                        placeholder="File Name"
                        ref={nameInputRef}
                    />
                </div>

                <div style={{ fontSize: '0.9em', marginTop: 3 }}>
                    {validation.filename && (
                        <span className="text-danger">{validation.filename}</span>
                    )}
                </div>
                {showDocumentCategory && (
                    <div className="form-group mb-2">
                        <label className="control-label" htmlFor="categories">
                            Document Category
                        </label>
                        <CheckmateSelect
                            isDisabled={isLoading}
                            isMulti={true}
                            onChange={handleDocumentCategoriesChange}
                            options={categoryDocumentTypeOptions}
                            value={categoryDocumentTypeOptions.filter((cdt) =>
                                local.documentCategoryTypeIds?.includes(cdt.id)
                            )}
                        />
                        {validation.categories && (
                            <span className="text-danger">{validation.categories}</span>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <LoadingButton
                    loading={saveDocumentMutation.isLoading}
                    loadingText="Saving..."
                    onClick={handleSaveDocument}
                    text="Save"
                    variant="primary"
                />
                <button className="btn btn-default" onClick={handleCancel} disabled={isLoading}>
                    Cancel
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default DocumentEditModal;
